import { createContext, useContext, useEffect, useState, type Dispatch, type PropsWithChildren, type SetStateAction } from 'react';

interface MediumSelectionProps {
    selectedMediums: string[];
    setSelectedMediums: Dispatch<SetStateAction<string[]>>;
}

function loadMediumsFromStorage(): string[] {
    if (typeof window === 'undefined') {
        return [];
    }
    const stored = sessionStorage.getItem('selectedMediums');
    if (stored) {
        try {
            const mediums = JSON.parse(stored);
            if (Array.isArray(mediums)) {
                return mediums;
            }
        } catch {
            // Ignore JSON parse errors
        }
    }
    return [];
}

function saveMediumsToStorage(mediums: string[]) {
    if (typeof window === 'undefined') {
        return;
    }
    sessionStorage.setItem('selectedMediums', JSON.stringify(mediums));
}

const MediumSelectionContext = createContext<MediumSelectionProps>({} as MediumSelectionProps);
export const useMediumSelectionContext = () => useContext(MediumSelectionContext);
export function MediumSelectionProvider(
    { fallbackSelection, children, availableMediums }: PropsWithChildren<
        { fallbackSelection?: string[]; availableMediums: string[] }
    >,
) {
    const [ selectedMediums, setSelectedMediums ] = useState<string[]>(fallbackSelection || []);
    const [ isRestored, setIsRestored ] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined' || isRestored) {
            return;
        }

        const storedMediums = loadMediumsFromStorage().filter(x => availableMediums.includes(x));
        if (storedMediums.length > 0) {
            setSelectedMediums(storedMediums);
            console.log(`✅ Restored ${storedMediums.length} mediums from sessionStorage`);
        }
        setIsRestored(true);
    }, [ availableMediums, isRestored ]);

    useEffect(() => {
        if (typeof window === 'undefined' || !isRestored) {
            return;
        }
        saveMediumsToStorage(selectedMediums);
        console.log(`✅ Saved ${selectedMediums.length} mediums to sessionStorage`);
    }, [ selectedMediums, isRestored ]);

    return (
        <MediumSelectionContext.Provider
            value={{
                selectedMediums,
                setSelectedMediums: (param: SetStateAction<string[]>) => {
                    if (typeof param === 'function') {
                        setSelectedMediums(old => {
                            const value = param(old);
                            return value.filter(x => availableMediums.includes(x));
                        });
                    } else {
                        setSelectedMediums(param.filter(x => availableMediums.includes(x)));
                    }
                }
            }}
        >
            {children}
        </MediumSelectionContext.Provider>
    );
}
